<template>
	<div class="content">
		<div class="section">
			<div class="section-tit">
				계약 관리
				<div class="right">
					<button type="button" class="btn btn-sm btn-primary" @click="openWrite(null)">계약 등록</button>
				</div>
			</div>

			<div class="search-top">
				<div class="left">
					<label class="input-label">상태</label>
					<select v-model="search_.status" class="form-control select sm">
						<option value="">전체</option>
						<option v-for="s in STATUS_LIST" :key="'st_' + s.value" :value="s.value">{{ s.label }}</option>
					</select>
					<label class="input-label">검색</label>
					<select v-model="search_.mode" class="form-control select sm">
						<option value="title">계약명</option>
						<option value="item">물건명/주소</option>
						<option value="buyer">매수인</option>
						<option value="seller">매도인</option>
					</select>
					<input class="form-control" type="text" v-model="search_.keyword" @keyup.enter="btnSearch" />
					<label class="input-label">잔금일</label>
					<input type="date" class="form-control sm" v-model="search_.sdate" />
					<span class="m-r--1">~</span>
					<input type="date" class="form-control sm" v-model="search_.edate" />
				</div>
				<div class="right">
					<button type="button" class="btn btn-primary" @click="btnSearch">검색</button>
					<button type="button" class="btn btn-secondary" @click="btnReset">초기화</button>
				</div>
			</div>
			<!-- BZ: 계약은 팀 단위로 열리지 않는다 — 참여자·등록자 또는 관리자만 본다. -->
			<div v-if="scopeLoaded && !scope.canPickStaff" class="txt-c--grey">
				※ <b>본인이 참여자로 지정된 계약과 본인이 등록한 계약</b>만 표시됩니다. (전체 열람은 관리자 권한)
			</div>
		</div>

		<div class="section">
			<div class="tab-btn">
				<button type="button" class="btn tab" v-bind:class="{ active: tab === 'list' }" @click="changeTabList">
					계약 목록
				</button>
				<button type="button" class="btn tab" v-bind:class="{ active: tab === 'cal' }" @click="changeTabCal">
					일정 캘린더
				</button>
			</div>

			<!-- BZ 직원별 탭 — 관리자만. 목록·캘린더 양쪽에 함께 적용된다.
			     참여자로 지정된 직원만 나오고(계약에 얽히지 않은 직원은 탭을 만들지 않는다),
			     진행 중 건수가 많은 직원이 앞에 온다. -->
			<div class="ct-staff" v-if="scope.canPickStaff">
				<span class="ct-staff--label">직원별</span>
				<button
					type="button"
					class="btn ct-staff--chip"
					v-bind:class="{ on: staffUid === null }"
					@click="pickStaff(null)"
				>
					전체
				</button>
				<button
					v-for="st in staffList"
					:key="'stf_' + st.mem_uid"
					type="button"
					class="btn ct-staff--chip"
					v-bind:class="{ on: Number(staffUid) === Number(st.mem_uid) }"
					@click="pickStaff(st.mem_uid)"
					:title="`${st.mem_name} ${st.mem_rank_name || ''} — 진행 ${st.ing_cnt}건 / 전체 ${st.contract_cnt}건`"
				>
					{{ st.mem_name }}
					<span class="ct-staff--cnt">{{ st.ing_cnt }}</span>
				</button>
				<span v-if="!staffList.length" class="txt-c--grey">계약에 참여한 직원이 없습니다.</span>
			</div>

			<!-- ── 목록 ─────────────────────────────────────────────────────────── -->
			<div class="tab-con" v-if="tab === 'list'">
				<div class="section-tit sub">검색결과 {{ totalCount }}건</div>
				<div class="table-wrap">
					<table class="table click">
						<colgroup>
							<col width="5%" /><col width="15%" /><col width="15%" /><col width="9%" />
							<col width="7%" /><col width="7%" /><col width="9%" /><col width="7%" />
							<col width="8%" /><col width="7%" /><col width="11%" />
						</colgroup>
						<tr>
							<th>번호</th>
							<th>계약명</th>
							<th>연결 매물</th>
							<th>매매금액</th>
							<th>계약일</th>
							<th>본계약일</th>
							<th>잔금일</th>
							<th>상태</th>
							<th>체크리스트</th>
							<th>참여자</th>
							<th>{{ staffUid ? '해당 직원 역할' : '내 역할' }} / 등록자</th>
						</tr>
						<tr v-for="c in itemList" :key="'ct_' + c.contract_uid" @click="openDetail(c.contract_uid)">
							<td>{{ c.contract_uid }}</td>
							<td class="txt-left">{{ c.title }}</td>
							<td class="txt-left">
								<template v-if="c.item_name">
									{{ c.item_name }}<br /><span class="txt-c--grey">{{ $normalizeAddr(c.item_jibun_addr) }}</span>
								</template>
								<span v-else-if="c.building_uid" class="txt-c--red">삭제된 매물</span>
								<span v-else class="txt-c--grey">미연결</span>
							</td>
							<td>{{ c.sell_price ? $formatMoney(c.sell_price, $MONEY_FORMAT_TYPE.MUCH) : '-' }}</td>
							<td>{{ $dateFormat(c.contract_date) }}</td>
							<td>{{ $dateFormat(c.main_contract_date) }}</td>
							<td>
								{{ $dateFormat(c.balance_date) }}
								<span v-if="c.balance_dday !== null" v-bind:class="ddayClass(c.balance_dday)" class="d-block">
									{{ ddayText(c.balance_dday) }}
								</span>
							</td>
							<td><span v-bind:class="statusClass(c.contract_status)">{{ STATUS_LABEL[c.contract_status] }}</span></td>
							<td>
								{{ c.chk_done }} / {{ c.chk_total }}
								<span v-if="c.chk_total > 0" class="txt-c--grey d-block">
									{{ Math.round((c.chk_done / c.chk_total) * 100) }}%
								</span>
							</td>
							<td>{{ c.member_cnt }}명</td>
							<!-- 관리자가 전체를 보면 본인 역할이 대개 비어 있다 → 등록자를 항상 병기해
							     "누구 계약인지" 를 알 수 있게 한다. -->
							<td>
								<span v-if="c.my_roles">{{ c.my_roles }}</span>
								<span v-else class="txt-c--grey">참여 아님</span>
								<span class="txt-c--grey d-block ct-reg">{{ c.reg_mem_name }} 등록</span>
							</td>
						</tr>
						<tr v-if="!itemList.length">
							<td colspan="11">등록된 계약이 없습니다.</td>
						</tr>
					</table>
				</div>

				<ul class="pagination b-pagination pagination-sm" v-if="pageData != null && pageData.list.length">
					<li class="page-item" v-bind:class="{ disabled: 1 == page }">
						<button type="button" class="page-link" @click="getItemList(1)"><b-icon-chevron-double-left /></button>
					</li>
					<li class="page-item" v-bind:class="{ disabled: pageData.first == null }">
						<button type="button" class="page-link" @click="pageData.first !== null ? getItemList(pageData.first) : ''">
							<b-icon-chevron-left />
						</button>
					</li>
					<li
						v-for="pg in pageData.list"
						:key="'pg_' + pg"
						class="page-item"
						v-bind:class="{ active: pageData.currentPage == pg }"
					>
						<button type="button" class="page-link" @click="getItemList(pg)">{{ pg }}</button>
					</li>
					<li class="page-item" v-bind:class="{ disabled: pageData.end == null }">
						<button type="button" class="page-link" @click="pageData.end !== null ? getItemList(pageData.end) : ''">
							<b-icon-chevron-right />
						</button>
					</li>
					<li class="page-item" v-bind:class="{ disabled: pageData.totalPage == page }">
						<button type="button" class="page-link" @click="getItemList(pageData.totalPage)">
							<b-icon-chevron-double-right />
						</button>
					</li>
				</ul>
			</div>

			<!-- ── C-6 월간 캘린더 ──────────────────────────────────────────────── -->
			<div class="tab-con" v-else>
				<div class="search-top">
					<div class="left">
						<button type="button" class="btn btn-sm btn-light" @click="moveMonth(-1)"><b-icon-chevron-left /></button>
						<label class="input-label">{{ calYear }}년 {{ calMonth }}월</label>
						<button type="button" class="btn btn-sm btn-light" @click="moveMonth(1)"><b-icon-chevron-right /></button>
						<button type="button" class="btn btn-sm btn-secondary" @click="goToday">오늘</button>
					</div>
					<div class="right">
						<!-- 슬라이드 11(본계약일+잔금일) vs 12(잔금일만) 충돌 → 둘 다 두고 체크박스로 on/off -->
						<label class="input-checkbox">
							<input type="checkbox" v-model="showBalance" />
							<span class="checkbox"></span>
							<span class="text">잔금일</span>
						</label>
						<label class="input-checkbox">
							<input type="checkbox" v-model="showMain" />
							<span class="checkbox"></span>
							<span class="text">본계약일</span>
						</label>
					</div>
				</div>

				<div class="table-wrap">
					<table class="table ct-cal">
						<tr>
							<th v-for="(d, i) in DOW" :key="'dw_' + i" v-bind:class="i === 0 ? 'txt-c--red' : ''">{{ d }}</th>
						</tr>
						<tr v-for="(week, wi) in calWeeks" :key="'wk_' + wi">
							<td v-for="(day, di) in week" :key="'dy_' + wi + '_' + di" class="ct-cal--cell">
								<div v-if="day" class="ct-cal--day" v-bind:class="{ today: day === todayStr }">
									<span v-bind:class="di === 0 ? 'txt-c--red' : ''">{{ Number(day.slice(8)) }}</span>
								</div>
								<div v-if="day">
									<button
										v-for="(e, ei) in eventsOf(day)"
										:key="'ev_' + day + '_' + ei"
										type="button"
										class="btn btn-xsm ct-cal--ev"
										v-bind:class="e.event_kind === 'balance' ? 'btn-danger' : 'btn-secondary'"
										@click="openDetail(e.contract_uid)"
									>
										{{ e.event_kind === 'balance' ? '잔금' : '본계약' }} · {{ e.title }}
										<span v-if="e.my_roles" class="ct-cal--role">{{ shortRole(e.my_roles) }}</span>
									</button>
								</div>
							</td>
						</tr>
					</table>
				</div>
			</div>
		</div>

		<!-- 계약 등록 팝업 -->
		<ContractForm
			v-if="writePopup"
			:contract-uid="null"
			@close="writePopup = false"
			@saved="onSaved"
		/>
	</div>
</template>

<script>
import ContractForm from '@/components/ContractForm.vue';

const STATUS_LABEL = { ing: '계약진행', ready: '잔금준비', done: '잔금완료', delay: '연기', cancel: '해제' };
const STATUS_LIST = Object.keys(STATUS_LABEL).map(k => ({ value: k, label: STATUS_LABEL[k] }));
const DOW = ['일', '월', '화', '수', '목', '금', '토'];

export default {
	name: 'ContractAdmin',
	components: { ContractForm },
	data() {
		const now = new Date();
		return {
			STATUS_LABEL,
			STATUS_LIST,
			DOW,
			tab: 'list',
			itemList: [],
			// canPickStaff 는 서버가 내려준다(관리자 여부). 프론트에서 추정하지 않는다.
			scope: { level: 'own', isMaster: false, canPickStaff: false },
			staffList: [],
			staffUid: null,
			// 칩을 빠르게 A→B 로 누르면 두 요청이 겹치고, 늦게 도착한 A 응답이 B 화면을 덮어
			// "B 를 선택했는데 A 의 계약과 역할이 보이는" 상태가 됐다. 요청마다 순번을 매겨
			// 최신 것만 반영한다(목록·캘린더 각각).
			listSeq: 0,
			calSeq: 0,
			staffSeq: 0,
			scopeLoaded: false,

			search: { status: '', mode: 'title', keyword: '', sdate: '', edate: '' },
			search_: { status: '', mode: 'title', keyword: '', sdate: '', edate: '' },

			page: 1,
			pageData: null,
			totalCount: 0,
			itemSize: 20,
			blockSize: 5,

			calYear: now.getFullYear(),
			calMonth: now.getMonth() + 1,
			calEvents: [],
			showBalance: true,
			showMain: true,

			writePopup: false,
		};
	},
	computed: {
		todayStr() {
			const d = new Date();
			return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
		},
		// 일요일 시작 6주 그리드 (캘린더 관행). 빈 칸은 null.
		calWeeks() {
			const first = new Date(this.calYear, this.calMonth - 1, 1);
			const last = new Date(this.calYear, this.calMonth, 0);
			const cells = [];
			for (let i = 0; i < first.getDay(); i += 1) cells.push(null);
			for (let d = 1; d <= last.getDate(); d += 1) {
				cells.push(`${this.calYear}-${String(this.calMonth).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
			}
			while (cells.length % 7 !== 0) cells.push(null);
			const weeks = [];
			for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
			return weeks;
		},
	},
	created() {
		this.getItemList(1);
	},
	methods: {
		// 직원별 칩 목록. 관리자가 아니면 서버가 403 을 주므로 아예 호출하지 않는다.
		//   한 번만 받아 캐시하지 않는다 — 담당자 지정으로 참여자가 자동 추가되면(CA) 칩이 바로
		//   생겨야 하고, 이 조회는 계약 수 기준 집계 한 방이라 가볍다.
		loadStaff() {
			if (!this.scope.canPickStaff) return;
			const seq = ++this.staffSeq;
			this.$apiGET('/admin/api/contract/staff/summary').then(re => {
				if (seq !== this.staffSeq) return;
				if (!re || !re.list) return;
				this.staffList = re.list;
			});
		},
		pickStaff(uid) {
			this.staffUid = uid === null ? null : Number(uid);
			if (this.tab === 'cal') this.loadCalendar();
			else this.getItemList(1);
		},
		// 역할명이 길어 캘린더 칸을 넘기므로 배지에는 핵심만 남긴다("매수 담당자" → "매수").
		shortRole(roles) {
			const first = String(roles).split(',')[0].trim();
			return first.replace(/\s*담당자$/, '').replace(/^담당\s*/, '');
		},
		btnSearch() {
			this.search = JSON.parse(JSON.stringify(this.search_));
			this.getItemList(1);
		},
		btnReset() {
			this.search_ = { status: '', mode: 'title', keyword: '', sdate: '', edate: '' };
			this.btnSearch();
		},
		getItemList(pg) {
			const q = new URLSearchParams({
				status: this.search.status || '',
				mode: this.search.mode,
				keyword: this.search.keyword || '',
				sdate: this.search.sdate || '',
				edate: this.search.edate || '',
				staff: this.staffUid || '',
				page: (pg - 1) * this.itemSize,
			});
			const seq = ++this.listSeq;
			this.$apiGET('/admin/api/contract/list?' + q.toString()).then(data => {
				if (seq !== this.listSeq) return;      // 더 최신 요청이 이미 나갔다
				if (!data || !data.item) return;
				this.scopeLoaded = true;
				this.itemList = data.item;
				this.totalCount = data.pageInfo.totalCount;
				this.scope = data.scope || this.scope;
				this.page = pg;
				this.pageData = this.$pageDataSetting(this.totalCount, this.itemSize, this.blockSize, this.page);
				this.loadStaff();
			});
		},
		openDetail(id) {
			this.$router.push({ name: 'ContractDetail', params: { id } });
		},
		openWrite() {
			this.writePopup = true;
		},
		onSaved(id) {
			this.writePopup = false;
			if (id) this.openDetail(id);
			else this.getItemList(this.page);
		},
		statusClass(s) {
			if (s === 'done') return 'txt-c--grey';
			if (s === 'cancel') return 'txt-c--red';
			if (s === 'delay') return 'txt-c--orange';
			if (s === 'ready') return 'txt-c--red';
			return 'txt-c--blue';
		},
		ddayText(d) {
			const n = Number(d);
			if (!Number.isFinite(n)) return '';
			if (n === 0) return 'D-DAY';
			return n > 0 ? `D-${n}` : `${Math.abs(n)}일 경과`;
		},
		ddayClass(d) {
			const n = Number(d);
			if (!Number.isFinite(n)) return '';
			if (n < 0) return 'txt-c--grey';
			if (n <= 7) return 'txt-c--red';
			if (n <= 30) return 'txt-c--orange';
			return 'txt-c--grey';
		},
		changeTabList() {
			this.tab = 'list';
			this.getItemList(1);
		},
		changeTabCal() {
			this.tab = 'cal';
			this.loadCalendar();
		},
		moveMonth(delta) {
			let y = this.calYear;
			let m = this.calMonth + delta;
			if (m < 1) {
				m = 12;
				y -= 1;
			} else if (m > 12) {
				m = 1;
				y += 1;
			}
			this.calYear = y;
			this.calMonth = m;
			this.loadCalendar();
		},
		goToday() {
			const now = new Date();
			this.calYear = now.getFullYear();
			this.calMonth = now.getMonth() + 1;
			this.loadCalendar();
		},
		loadCalendar() {
			const sdate = `${this.calYear}-${String(this.calMonth).padStart(2, '0')}-01`;
			const last = new Date(this.calYear, this.calMonth, 0).getDate();
			const edate = `${this.calYear}-${String(this.calMonth).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
			const staff = this.staffUid ? `&staff=${this.staffUid}` : '';
			const seq = ++this.calSeq;
			this.$apiGET(`/admin/api/contract/calendar?sdate=${sdate}&edate=${edate}${staff}`).then(re => {
				if (seq !== this.calSeq) return;
				if (!re || !re.list) return;
				this.calEvents = re.list;
			});
		},
		eventsOf(day) {
			return this.calEvents.filter(
				e =>
					e.event_date === day &&
					((e.event_kind === 'balance' && this.showBalance) || (e.event_kind === 'main' && this.showMain)),
			);
		},
	},
};
</script>

<style lang="scss">
// 값의 출처는 DESIGN.md 하나뿐 — 색·간격을 이 파일에 숫자로 적지 않는다.
@import "@/assets/css/design-tokens.scss";

// 직원별 필터는 상단 탭(계약 목록 / 일정 캘린더)의 하위 2차 필터다. 같은 탭 모양으로 두 줄을
// 쌓으면 어느 쪽이 상위인지 읽히지 않으므로 칩으로 낮춘다. 참여 직원이 많으면 줄바꿈한다.
.ct-staff {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 6px;
	margin-bottom: 14px;
	// 참여 직원이 늘어도 표를 아래로 계속 밀지 않게 3줄 정도에서 스크롤한다.
	max-height: 96px;
	overflow-y: auto;
	&--label {
		font-size: 12px;
		font-weight: 600;
		color: $dt-color-ink-muted;
		margin-right: 2px;
	}
	// bootstrap 의 .btn 과 특이도가 같으면(둘 다 0,1,0) 로드 순서에 따라 배경·테두리가 덮여
	// 칩이 그냥 글자처럼 보인다(실측: background transparent). 부모를 함께 써서 한 단계 올린다.
	.ct-staff--chip {
		height: 24px;
		padding: 0 10px;
		font-size: 12px;
		line-height: 1;
		border: 1px solid $dt-color-line-strong;
		border-radius: $dt-rounded-full;
		background: $dt-color-canvas;
		color: $dt-color-ink-muted;
		&:hover {
			border-color: $dt-color-ink-faint;
			color: $dt-color-ink;
		}
		&.on {
			background: $dt-color-primary-ink;
			border-color: $dt-color-primary-ink;
			color: #fff;
		}
	}
	// 진행 중 건수 — 칩 안의 보조 수치. 0 건인 직원도 남겨야(계약이 끝난 직원) 조회가 가능하다.
	&--cnt {
		font-size: 11px;
		margin-left: 4px;
		opacity: 0.75;
	}
}
// 목록의 등록자 표기 — 역할 아래 한 단계 낮춘 보조 정보.
.ct-reg {
	font-size: 11px;
}
.ct-cal {
	table-layout: fixed;
	&--cell {
		height: 96px;
		vertical-align: top !important;
		text-align: left !important;
		padding: 4px !important;
	}
	&--day {
		font-weight: 600;
		margin-bottom: 2px;
		&.today span {
			background: #0d6efd;
			color: #fff;
			border-radius: 50%;
			padding: 1px 6px;
		}
	}
	&--ev {
		display: block;
		width: 100%;
		text-align: left;
		margin-bottom: 2px;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}
	// 역할 배지 — 그 계약에서 내(또는 선택한 직원의) 역할. 색은 버튼 배경 위라 흰 글씨 고정.
	&--role {
		border: 1px solid rgba(255, 255, 255, 0.6);
		border-radius: 4px;
		padding: 0 3px;
		margin-left: 3px;
		font-size: 10px;
	}
}
</style>
